
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, X, Settings, Check } from 'lucide-react';
import { Link } from 'react-router-dom';

const CookieConsent: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('vextora_cookie_consent');
    if (!consent) {
      setIsVisible(true);
    } else {
      const parsedConsent = JSON.parse(consent);
      if (parsedConsent.analytics) {
        triggerAnalytics();
      }
    }
  }, []);

  const triggerAnalytics = () => {
    console.log('Analytics scripts initialized.');
    // Example: window.gtag('config', 'UA-XXXXX-Y');
  };

  const handleAcceptAll = () => {
    const consent = { analytics: true, necessary: true, timestamp: new Date().toISOString() };
    localStorage.setItem('vextora_cookie_consent', JSON.stringify(consent));
    triggerAnalytics();
    setIsVisible(false);
  };

  const handleRejectAll = () => {
    const consent = { analytics: false, necessary: true, timestamp: new Date().toISOString() };
    localStorage.setItem('vextora_cookie_consent', JSON.stringify(consent));
    setIsVisible(false);
  };

  const handleSavePreferences = () => {
    const consent = { analytics: analyticsEnabled, necessary: true, timestamp: new Date().toISOString() };
    localStorage.setItem('vextora_cookie_consent', JSON.stringify(consent));
    if (analyticsEnabled) triggerAnalytics();
    setShowPreferences(false);
    setIsVisible(false);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          {/* Main Banner */}
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className="fixed bottom-6 left-6 right-6 md:left-auto md:max-w-md z-[100]"
          >
            <div className="glass-card p-6 rounded-[2rem] border border-white/10 shadow-2xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <Shield size={20} className="text-purple-400" />
                </div>
                <h3 className="font-orbitron font-bold text-white">Your privacy preferences</h3>
              </div>
              
              <p className="text-sm text-gray-400 leading-relaxed mb-6">
                Vextora Media uses essential cookies to ensure our website functions correctly. With your permission, we would also like to use analytics cookies to help us improve your experience.
              </p>

              <div className="flex flex-col gap-3">
                <button
                  onClick={handleAcceptAll}
                  className="w-full bg-purple-600 hover:bg-purple-500 text-white font-bold py-3 rounded-xl transition-all text-sm"
                >
                  Accept analytics cookies
                </button>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={handleRejectAll}
                    className="bg-white/5 hover:bg-white/10 text-white font-bold py-3 rounded-xl border border-white/10 transition-all text-sm"
                  >
                    Reject all
                  </button>
                  <button
                    onClick={() => setShowPreferences(true)}
                    className="bg-white/5 hover:bg-white/10 text-white font-bold py-3 rounded-xl border border-white/10 transition-all text-sm flex items-center justify-center gap-2"
                  >
                    <Settings size={14} />
                    Manage
                  </button>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-white/5 flex justify-center gap-4">
                <Link to="/privacy" className="text-[10px] uppercase tracking-widest text-gray-500 hover:text-white">Privacy Policy</Link>
                <Link to="/cookies" className="text-[10px] uppercase tracking-widest text-gray-500 hover:text-white">Cookie Policy</Link>
              </div>
            </div>
          </motion.div>

          {/* Preferences Modal */}
          {showPreferences && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="fixed inset-0 z-[110] bg-[#0A0612]/80 backdrop-blur-md flex items-center justify-center p-6"
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="glass-card w-full max-w-lg p-8 rounded-[3rem] border border-white/10 relative"
              >
                <button 
                  onClick={() => setShowPreferences(false)}
                  className="absolute top-6 right-6 text-gray-500 hover:text-white"
                >
                  <X size={24} />
                </button>

                <h2 className="text-2xl font-orbitron font-bold text-white mb-8">Cookie Preferences</h2>

                <div className="space-y-6 mb-10">
                  {/* Necessary */}
                  <div className="p-5 rounded-2xl bg-white/5 border border-white/5">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-bold text-white">Strictly Necessary</h4>
                      <span className="text-[10px] uppercase font-bold text-purple-400 bg-purple-400/10 px-2 py-1 rounded">Always Active</span>
                    </div>
                    <p className="text-xs text-gray-400 leading-relaxed">
                      These cookies enable core functionality such as security, network management, and accessibility. You may disable these by changing your browser settings.
                    </p>
                  </div>

                  {/* Analytics */}
                  <div className="p-5 rounded-2xl bg-white/5 border border-white/5">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-bold text-white">Analytics Cookies</h4>
                      <button
                        onClick={() => setAnalyticsEnabled(!analyticsEnabled)}
                        className={`w-12 h-6 rounded-full transition-all relative ${analyticsEnabled ? 'bg-purple-600' : 'bg-gray-700'}`}
                      >
                        <motion.div 
                          animate={{ x: analyticsEnabled ? 26 : 2 }}
                          className="absolute top-1 left-0 w-4 h-4 bg-white rounded-full shadow-sm" 
                        />
                      </button>
                    </div>
                    <p className="text-xs text-gray-400 leading-relaxed">
                      We use these cookies to collect information about how visitors use our site, helping us compile reports and improve the website.
                    </p>
                  </div>
                </div>

                <button
                  onClick={handleSavePreferences}
                  className="w-full bg-white text-black font-bold py-4 rounded-2xl hover:bg-gray-200 transition-all flex items-center justify-center gap-2"
                >
                  <Check size={18} />
                  Save my preferences
                </button>
              </motion.div>
            </motion.div>
          )}
        </>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
